import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import { Send, History, Users, AlertTriangle, Radio } from "lucide-react";

type TargetType = "all" | "by_plan" | "by_status" | "manual";
type Channel = "email" | "in_app" | "both";

const PLANS = ["STARTER", "PRO", "ENTERPRISE"];
const STATUSES = ["TRIAL", "ACTIVE", "SUSPENDED", "CANCELLED"];
const CHANNELS: { value: Channel; label: string }[] = [
  { value: "in_app", label: "In-app uniquement" },
  { value: "email", label: "Email uniquement" },
  { value: "both", label: "Email + In-app" },
];

export default function BroadcastCenter() {
  const [targetType, setTargetType] = useState<TargetType>("all");
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [channel, setChannel] = useState<Channel>("in_app");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [confirmSend, setConfirmSend] = useState(false);
  const [sentMsg, setSentMsg] = useState<string | null>(null);

  const filter = {
    type: targetType,
    plans: targetType === "by_plan" ? selectedPlans : undefined,
    statuses: targetType === "by_status" ? selectedStatuses : undefined,
  };

  const { data: preview } = trpc.admin.broadcast.preview.useQuery(
    { filter },
    { enabled: true }
  );

  const { data: history, isLoading: histLoading, refetch: refetchHistory } =
    trpc.admin.broadcast.history.useQuery({ page: 1, limit: 20 });

  const sendBroadcast = trpc.admin.broadcast.send.useMutation({
    onSuccess: (data) => {
      setSentMsg(`Broadcast envoyé à ${data.recipientCount} destinataire(s)`);
      setSubject("");
      setBody("");
      setConfirmSend(false);
      refetchHistory();
    },
    onError: () => setConfirmSend(false),
  });

  const toggleItem = (item: string, list: string[], setList: (l: string[]) => void) => {
    setList(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  };

  const handleSend = () => {
    if (!subject.trim() || !body.trim()) return;
    sendBroadcast.mutate({ subject, body, filter, channel, force: false });
  };

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Radio className="h-7 w-7" />
            Broadcast & Communication
          </h1>
          <p className="text-muted-foreground">Envoyer des messages groupés à vos tenants</p>
        </div>

        {/* Composer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Composer un message
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {sentMsg && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                {sentMsg}
              </div>
            )}

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium mb-1">Sujet</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Objet du message"
                maxLength={500}
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium mb-1">Corps du message</label>
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-none min-h-[120px]"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Contenu du message…"
                maxLength={10000}
              />
              <div className="text-xs text-muted-foreground text-right mt-1">{body.length}/10000</div>
            </div>

            {/* Channel */}
            <div>
              <label className="block text-sm font-medium mb-2">Canal</label>
              <div className="flex gap-2 flex-wrap">
                {CHANNELS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setChannel(c.value)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      channel === c.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Targeting */}
            <div>
              <label className="block text-sm font-medium mb-2">Ciblage</label>
              <div className="flex gap-2 flex-wrap mb-3">
                {(["all", "by_plan", "by_status"] as TargetType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTargetType(t)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      targetType === t
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    {t === "all" ? "Tous" : t === "by_plan" ? "Par plan" : "Par statut"}
                  </button>
                ))}
              </div>

              {targetType === "by_plan" && (
                <div className="flex gap-2 flex-wrap">
                  {PLANS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => toggleItem(p, selectedPlans, setSelectedPlans)}
                      className={`px-3 py-1 rounded text-sm border transition-colors ${
                        selectedPlans.includes(p) ? "bg-blue-600 text-white border-blue-600" : "border-border hover:bg-muted"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
              {targetType === "by_status" && (
                <div className="flex gap-2 flex-wrap">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleItem(s, selectedStatuses, setSelectedStatuses)}
                      className={`px-3 py-1 rounded text-sm border transition-colors ${
                        selectedStatuses.includes(s) ? "bg-blue-600 text-white border-blue-600" : "border-border hover:bg-muted"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Preview */}
            {preview && (
              <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-3 text-sm">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>
                  <strong>{preview.userCount.toLocaleString("fr-FR")}</strong> destinataire(s) dans{" "}
                  <strong>{preview.companyCount}</strong> entreprise(s)
                </span>
              </div>
            )}

            {/* Confirm & Send */}
            {!confirmSend ? (
              <Button
                onClick={() => setConfirmSend(true)}
                disabled={!subject.trim() || !body.trim() || sendBroadcast.isPending}
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" />
                Préparer l'envoi
              </Button>
            ) : (
              <div className="border border-yellow-400 bg-yellow-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2 text-yellow-800">
                  <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Confirmer l'envoi en masse</div>
                    <div className="text-sm mt-1">
                      Ce message sera envoyé à{" "}
                      <strong>{(preview?.userCount ?? 0).toLocaleString("fr-FR")} utilisateurs</strong>. Cette action est irréversible.
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSend}
                    disabled={sendBroadcast.isPending}
                    variant="destructive"
                  >
                    {sendBroadcast.isPending ? "Envoi en cours…" : "Confirmer et envoyer"}
                  </Button>
                  <Button variant="outline" onClick={() => setConfirmSend(false)}>
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* History */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <History className="h-4 w-4" />
            Historique des broadcasts
          </h2>
          <Card>
            <CardContent className="p-0">
              {histLoading ? (
                <div className="p-6 space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : !history?.items.length ? (
                <div className="p-6 text-center text-muted-foreground">Aucun broadcast envoyé</div>
              ) : (
                <div className="divide-y">
                  {history.items.map((item: any) => (
                    <div key={item.id} className="flex items-start justify-between px-4 py-3 text-sm gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.subject}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.body}</div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <Badge variant="secondary">{item.channel}</Badge>
                        <span className="text-muted-foreground text-xs">{item.recipientCount} dest.</span>
                        <span className="text-muted-foreground text-xs">{formatDate(item.sentAt ?? item.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
